import { zodResolver } from '@hookform/resolvers/zod';
import { Combobox } from '@optools/components/combobox/combobox';
import { Spinner } from '@optools/components/loading/spinner';
import { Button } from '@optools/components/ui/button';
import { DialogFooter } from '@optools/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { ProgramSource, Region } from '@optools/services/operator-gateway/types/generated/graphql';
import { useCallSignsQuery } from '@optools/store/statsApiSlice';
import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ProgramSourceSelect } from './program-source-select';

// Sentinel region value that omits the region parameter when creating an override
export const ALL_REGIONS = 'ALL_REGIONS';

const formSchema = z.object({
  callSign: z.string().min(1, { error: 'Call sign is required' }),
  gameSource: z.enum(ProgramSource).nullable().optional(),
  postgameSource: z.enum(ProgramSource).nullable().optional(),
  pregameSource: z.enum(ProgramSource).nullable().optional(),
  region: z.union([z.enum(Region), z.literal(ALL_REGIONS)]),
});

export type BroadcastSourceOverrideFormValues = z.infer<typeof formSchema>;

export interface BroadcastSourceOverrideFormProps {
  allowAllRegions?: boolean;
  defaultValues?: BroadcastSourceOverrideFormValues;
  disabled?: boolean;
  disableRegion?: boolean;
  onCancel: () => void;
  onSubmit: (values: BroadcastSourceOverrideFormValues) => void;
}

export function BroadcastSourceOverrideForm({
  allowAllRegions = false,
  defaultValues,
  disabled = false,
  disableRegion = false,
  onCancel,
  onSubmit,
}: BroadcastSourceOverrideFormProps) {
  const {
    data: callSignData,
    error: callSignError,
    isLoading: callSignIsLoading,
  } = useCallSignsQuery();

  const callSignValues = useMemo(() => {
    if (!callSignData) {
      return [];
    }

    const formattedCallSigns = [
      ...new Set(callSignData.filter((item) => item).map((item) => item.trim())),
    ];
    return formattedCallSigns.sort().map((item) => {
      return {
        label: item,
        value: item,
      };
    });
  }, [callSignData]);

  const form = useForm<BroadcastSourceOverrideFormValues>({
    defaultValues: {
      callSign: '',
      gameSource: ProgramSource.UNKNOWN,
      postgameSource: ProgramSource.UNKNOWN,
      pregameSource: ProgramSource.UNKNOWN,
      region: allowAllRegions ? ALL_REGIONS : Region.EAST,
      ...defaultValues,
    },
    disabled,
    resolver: zodResolver(formSchema),
  });

  return (
    <Form {...form}>
      <form
        className="mt-2 flex flex-col gap-6"
        onReset={() => {
          form.reset();
          onCancel();
        }}
        onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-wrap items-center justify-start gap-2">
          <FormField
            control={form.control}
            disabled={disabled}
            name="callSign"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Call Sign</FormLabel>
                  <FormControl className="w-44">
                    {callSignIsLoading ? (
                      <Spinner />
                    ) : callSignError ? (
                      <Input {...field} />
                    ) : (
                      <Combobox
                        data={callSignValues}
                        disabled={field.disabled}
                        useIndexInKey={true}
                        value={field.value}
                        onValueChange={field.onChange}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            disabled={disabled || disableRegion}
            name="region"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel className="flex items-center gap-1">
                    Region
                    {disableRegion && (
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <Info
                            aria-label="Region info"
                            className="size-3.5 text-muted-foreground"
                          />
                        </TooltipTrigger>
                        <TooltipContent sideOffset={4}>
                          Region cannot be updated once override is created
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </FormLabel>
                  <Select
                    disabled={field.disabled}
                    value={field.value}
                    onValueChange={field.onChange}>
                    <FormControl className="w-44">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {allowAllRegions && <SelectItem value={ALL_REGIONS}>All Regions</SelectItem>}
                      <SelectItem value={Region.EAST}>{Region.EAST}</SelectItem>
                      <SelectItem value={Region.CENTRAL}>{Region.CENTRAL}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
        <div className="flex flex-wrap items-center justify-start gap-2">
          <FormField
            control={form.control}
            disabled={disabled}
            name="pregameSource"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Pre-Game Source</FormLabel>
                  <FormControl>
                    <ProgramSourceSelect
                      disabled={field.disabled}
                      value={field.value ?? ProgramSource.UNKNOWN}
                      onValueChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            disabled={disabled}
            name="gameSource"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Game Source</FormLabel>
                  <FormControl>
                    <ProgramSourceSelect
                      disabled={field.disabled}
                      value={field.value ?? ProgramSource.UNKNOWN}
                      onValueChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            disabled={disabled}
            name="postgameSource"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Post-Game Source</FormLabel>
                  <FormControl>
                    <ProgramSourceSelect
                      disabled={field.disabled}
                      value={field.value ?? ProgramSource.UNKNOWN}
                      onValueChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        <DialogFooter>
          <Button disabled={form.formState.disabled} type="reset" variant="secondary">
            Cancel
          </Button>
          <Button
            aria-label="submit"
            disabled={form.formState.disabled}
            loading={form.formState.disabled}
            type="submit">
            Submit
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
